<script setup lang="ts">
	import { ref, computed, onMounted, watch } from 'vue';

	import StringPrinter from '../table/StringPrinter.vue';
	import DatePrinter from '../table/DatePrinter.vue';
	import NumberPrinter from '../table/NumberPrinter.vue';
	import HtmlPrinter from '../table/HtmlPrinter.vue';
	import KeyPrinter from '../table/KeyPrinter.vue';
	import BooleanPrinter from '../table/BooleanPrinter.vue';
	import SummaryPrinter from '../table/SummaryPrinter.vue';
	import SequencePrinter from '../table/SequencePrinter.vue';
	import FolderPrinter from '../table/FolderPrinter.vue';
	import FilePrinter from '../table/FilePrinter.vue';
	import HeaderColumn from '../table/HeaderColumn.vue';
	import { useBaseTable } from '../table/BaseTable';
	import {injectEnv} from "@fd/Environment";
	import SymIcon from "@fd/helpers/SymIcon.vue";


	const props = defineProps({
		tableColumns: Array,
		tableRows: Array,
		tableTotalCount: 0,
		restApi: { type: String, required: false, default: "api/files/fileBrowser" },
		tableSetting: Object,
		disableClientStorage: Boolean
	});
	
	//setup
	if (props.tableSetting) {
		let pageStartIndex = props.tableSetting.page*props.tableSetting.pageSize;
		if (pageStartIndex>props.tableTotalCount) pageStartIndex=props.tableTotalCount;
		props.tableSetting.pageStartIndex = pageStartIndex;
	}
	else {
		//fake empty tableSetting
		props.tableSetting = {
			pageSize: 10,
			page: 0,
			pageStartIndex: 0,
			tableClass: "agenda",
			evenRowClass: "evenrow",
			oddRowClass:"oddrow",
			sortColumnClass: "sortedHeader",
			emptyMessage: "No data loaded"
		}
		props.tableTotalCount=0;
	}
	
	const emit = defineEmits(['selectFile,close']);
	
	const {	setting,sortColumn,sortOrderAsc,pageSize,pageStartIndex,
			columns,totalCount,header,errorMessage,
			preparedRows,pageEndIndex,isEmpty,isError,
			sortBy,rowClass,rowStyle,sortStyle,
			firstPage,lastPage,nextPage,prevPage,loadTable,
			refresh,refreshRestApi } = useBaseTable(props, initTableData);
		
	interface PathItem {
		id: number;
		name: string;
	}
		
	const path = ref<PathItem[]>(null);
	const loading = ref(true);
	const storageKey: string = "CRM.FileBrowser";
	
	const numberOfColumns = computed(() => {
		return headerColumns.value?headerColumns.value.length:0
	});
	
	const headerColumns = computed(() => {
		return columns.value?columns.value.filter(column => (!column.rowType && column.id!=='EDIT' && column.id!=='DELETE')):[];
	});
	
	const numberOfDataColumns = computed(() => {
		return  numberOfColumns.value>0? numberOfColumns.value:0;
	});
	
	const env = injectEnv();
	const bundleTitle = env.msg("menu.viewFileSystem");
	
	function initTableData(data): void {
		if (path.value==null && data.path) {
			//let newPath: PathItem[] = data.path;
			console.log("Init path from server:"+JSON.stringify(data.path));
			path.value = data.path;
		}
	}
	
	function getComponent(componentName: string, folder: boolean) 
	{
		switch(componentName) {
			case "DatePrinter":  return DatePrinter;
			case "NumberPrinter": return NumberPrinter;
			case "HtmlPrinter": 
				if (folder) return FolderPrinter;
				else if (folder==false) return FilePrinter;
				else return HtmlPrinter;
			case "KeyPrinter": return KeyPrinter;
			case "BooleanPrinter": return BooleanPrinter;
			case "SummaryPrinter": return SummaryPrinter;
			case "StringPrinter": 
			case "SequencePrinter": 
			case "SelectPrinter": return StringPrinter;
			default:
				console.log("Undefined component:"+componentName);
				errorMessage.value = "Undefined component:"+componentName;
				return null;
		}
	}
	
	function selectFile(id: string, name: string, mime: string): void
	{
		//console.log("fbid:"+id);
		emit('selectFile',id,name,mime);
		emit('close');
	}
	
	function openFolder(id: string, name: string): void 
	{
		const item: PathItem = {id: id, name: name};
		path.value.push(item);
		refreshFolder();
	}
	
	function openRoot(): void
	{
		path.value=[];
		refreshFolder();
	}
	
	function closeFolder(id?: number): void
	{
		if (id) {
			const index = path.value.findIndex(item=>item.id===id);
			if (index>=0) {
				path.value=path.value.slice(0,index+1);
			}
		}
		else {
			path.value.pop();
		}
		refreshFolder();
	}
	
	function refreshFolder(): void
	{
		const item: PathItem = path.value.length>0?path.value.slice(-1)[0]:{id:0,name: "/"};
		refresh(openFolderRestApi(item));
	}
	
	function openFolderRestApi(pathItem: PathItem): string
	{
		let url: string = refreshRestApi();
		if (url.indexOf("?")<0) url +="?";
		url += "&folder="+pathItem.id;
		console.log("openFolder url:"+url);
		return url;
	}
	
	function saveToSession(newPath: PathItem[]): void {
		//console.log("saving path:"+JSON.stringify(newPath));
		sessionStorage.setItem(storageKey,JSON.stringify(newPath));
	}

	watch(path, (newPath: PathItem[])=> {
			saveToSession(newPath);
		},
		{ deep: !props.disableClientStorage }
	);

	onMounted(() => {
		if (!props.disableClientStorage) {
			let storedPath: string = sessionStorage.getItem(storageKey);
			if (storedPath!=null) {
				console.log("restored path from sessionStorage:"+storedPath);
				let newPath = JSON.parse(storedPath);
				const item: PathItem = newPath.length>0?newPath.slice(-1)[0]:{id:0,name: "/"};
				path.value=newPath;
				return loadTable(openFolderRestApi(item)).then((result)=>{loading.value=false;});
			}
		}
		
		if (Boolean(props.restApi)) { //not empty endpoint - use loading data
			return loadTable().then((result)=>{
				if (path.value==null) {path.value=[];}
				loading.value=false;
			});
		}
		//console.log("Mounted: rows:"+this.totalCount+" / pageSize:"+this.pageSize+" / startIndex:"+this.pageStartIndex+" / endIndex:"+this.pageEndIndex);
	});
	
	
</script>
<template>
	<span><slot></slot></span>
	<div v-if="isError" class="errors">
		<span class="perr">{{errorMessage}}</span>
	</div>
	<template v-if="loading">
		<span>Loading...</span>
	</template>
	<template v-else>
		<table :class="setting.tableClass">
			<thead>
				<tr>
					<th :colspan="numberOfColumns" style="border-bottom: none; font-weight: bold;" >
						<a href="#" @click.prevent="openRoot()"><sym-icon name="volume"/> {{bundleTitle}}</a>
						<span v-for="item in path"><a href="#" @click.prevent="closeFolder(item.id)">/{{item.name}}</a></span>
					</th>
				</tr>
				<tr>
					<header-column v-for="column in headerColumns" v-bind="column" v-model="header[column.id]"
						:sortColumn="sortColumn" :sortOrderAsc="sortOrderAsc" :sortColumnClass="setting.sortColumnClass"
						@refresh="refreshFolder" @sortBy="sortBy"/>
				</tr>
			</thead>
			<tbody v-if="isEmpty && path.length==0"><tr><td :colspan="numberOfColumns"><b>{{setting.emptyMessage}}</b></td></tr></tbody>
			<tbody v-else>
				<tr class="oddrow" v-show="path.length>0">
					<FolderPrinter value='..' styleClass="boldText" @open-folder="closeFolder()" :sortStyle="sortStyle({id:'name'}, 0)" />
					<template v-for="column in headerColumns">
					  <td v-if="column.id!=='name'" :style="sortStyle(column, 0)" ></td>
					</template>
				</tr>
				<template v-for="(row, index) in preparedRows" :key="row.primaryKey">
					<tr :class="rowClass(row, index)" :style="rowStyle(row)">
						<template v-for="column in headerColumns">
								<component :is="getComponent(column.component,row.folder)" v-bind="row[column.id]" :sortStyle="sortStyle(column, index)" 
								@emit-error="(error)=>errorMessage=error" @open-folder="openFolder(row.primaryKey,row.name.rowValue)"
								@select-file="selectFile(row.primaryKey,row.name.rowValue,row.type.rowValue)"/>
						</template>
					</tr>
				</template>
			</tbody>
			<tfoot v-if="!setting.isHidePageSetting">
				<tr>
					<th class="tablepager" :colspan="numberOfColumns">
					<div class="tablepager-content">

					<span class="pagercontrols">
						<span :class="{ disabledclick: pageStartIndex <= 0,  disabled: pageStartIndex <= 0, fi: true, activeclick: true}" style="width:22px" @click="firstPage()"></span>
						<span :class="{ disabledclick: pageStartIndex <= 0, disabled: pageStartIndex <= 0, fi: true, activeclick: true }" style="width:22px" @click="prevPage()"></span>
						<span :class="{ disabledclick: pageStartIndex >= totalCount-pageSize, disabled: pageStartIndex >= totalCount-pageSize, fi: true, activeclick: true }" style="width:22px" @click="nextPage()"></span>
						<span :class="{ disabledclick: pageStartIndex >= totalCount-pageSize,  disabled: pageStartIndex >= totalCount-pageSize, fi: true, activeclick: true }" style="width:22px" @click="lastPage()"></span>

					<b><span style="margin-left:4px" class="problemAgenda_from">{{pageStartIndex+(isEmpty?0:1)}}</span> - <span class="problemAgenda_to">{{pageEndIndex}}</span></b>
					<span style="margin-left:4px;opacity:0.7">({{totalCount}} záznamů)</span>
					<span style="margin-left:4px" v-if="!isEmpty">
						<select v-model.number="pageSize">
							<option value="10">10</option>
							<option value="20">20</option>
							<option value="50">50</option>
							<option value="100">100</option>
						</select>
					</span>
					</span>
					</div>
					</th>
				</tr>
			</tfoot>
		</table>
	</template>
</template>
